import * as assert from 'assert';
import { inject } from 'mobx-react';
import * as React from 'react';
import * as Dropzone from 'react-dropzone';
import { RouteComponentProps } from 'react-router';
import {
    AddPhotoConstants as ui,
    ACCEPT_IMAGE_TYPE,
    FRESHDESK,
    MAX_PHOTOS_PER_UPLOAD
} from '../../Config/UIConstants';
import { CommonActionButton } from '../../SharedComponents/CommonActionButton';
import { CommonTextInput } from '../../SharedComponents/CommonTextInput';
import { DuplicatePhotoPrompt } from '../../SharedComponents/DuplicatePhotoPrompt';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { InformationIcon } from '../../SharedComponents/InformationIcon';
import { ModalActionButtonBar } from '../../SharedComponents/ModalActionButtonBar';
import { IUploadedPhoto } from '../../Stores/UserPhotoStore';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { ModalXIconInverse, UploadPhotoIcon } from '../../images/images';
import { compressImage, getNextRotation, getRotatedFileFromImage } from '../../util/resizeImage';
import { styles } from '../PhotosPage/AddPhotosStyle';

import { Exif } from '../AddPhotosPage/AbstractAddPhotosPage';

import { Errors } from '../../SharedComponents/Errors/Errors';

export const THUMBNAIL_WIDTH_PIXELS = 450;

export interface ISimplePhotoData {
    name: string;
    url: string;
}

export interface IPossibleDuplicate {
    originalPhoto: ISimplePhotoData;
    newPhoto: IUploadedPhoto;
    isDuplicate: boolean | undefined;
    tempID: number;
}

interface IProps {
    store?: ApplicationStore;
    onPhotosChanged(photos: Array<any>): void;
    onAfterPhotosAdded(photoIds: Array<number>): void;
}

interface IState {
    images: Array<IUploadedPhoto>;
    currentImageFocusIndex: number;
    indexOfImageToUpload: number;
    idsToAddToStory: Array<number>;
    showUploadProgress: boolean;
    fetchingAlbums: boolean;
    showExtraPhotoNumber?: number;
    possibleDuplicates: Array<IPossibleDuplicate>;
    duplicateIndex: number;
    errors: Array<string>;
    showUploadErrorConfirmButton: boolean;
}

export enum ImageDescription {
    Location = 'Location',
    Date = 'Original Date',
    FileName = 'File Name',
}

@inject('store')
export class StoryPhotoUploader extends React.Component<RouteComponentProps<any> & IProps, IState> {
    public state: IState = {
        images: [],
        currentImageFocusIndex: 0,
        indexOfImageToUpload: 0,
        idsToAddToStory: [],
        showUploadProgress: false,
        fetchingAlbums: false,
        possibleDuplicates: [],
        duplicateIndex: 0,
        errors: [],
        showUploadErrorConfirmButton: false,
    };

    private dropzoneRef: any;

    private async onDrop(files: Array<any>): Promise<void> {
        console.assert(files, 'Parameter is undefined');

        const filesWithInfo = await Exif.getUpdatedImageFiles(files);

        this.setState({errors: []});

        filesWithInfo.forEach((file, index) => {
            if (file.error) {
                let error = `${file.filename} could not be uploaded: ${file.error}`;
                this.setState({errors: [...this.state.errors, error]});
            }
        });

        if (this.state.images.length + filesWithInfo.length > MAX_PHOTOS_PER_UPLOAD) {
            alert(ui.MAX_UPLOAD_LIMIT);
            return;
        }

        const { uniquePhotos, possibleDuplicates } = await this.checkForDuplicateFileNames(
            filesWithInfo,
        );
        this.setState({
            images: [...this.state.images, ...uniquePhotos.filter(file => !file.error)],
            possibleDuplicates
        });
    }

    private onClickDuplicate = (isDuplicate: boolean) => {
        let index = this.state.duplicateIndex;
        const duplicates = [...this.state.possibleDuplicates];

        duplicates[index].isDuplicate = isDuplicate;

        index += 1;

        if (index >= duplicates.length) {
            const uniqueFiles = this.getUniqueFilesForDuplicates(duplicates);
            this.setState(
                {
                    images: [...this.state.images, ...uniqueFiles],
                    possibleDuplicates: [],
                    duplicateIndex: 0,
                },
                () => {
                    this.props.onPhotosChanged(this.state.images);
                },
            );
        } else {
            this.setState({ possibleDuplicates: duplicates, duplicateIndex: index });
        }
    }

    private getUniqueFilesForDuplicates(duplicates: Array<IPossibleDuplicate>): Array<IUploadedPhoto> {
        const possibleDuplicateGroupedByFile: { [index: number]: Array<IPossibleDuplicate> } = {};
        duplicates.forEach(d => {
            if (possibleDuplicateGroupedByFile[d.tempID] === undefined) {
                possibleDuplicateGroupedByFile[d.tempID] = [d];
            } else {
                possibleDuplicateGroupedByFile[d.tempID].push(d);
            }
        });

        const uniqueFiles: Array<IUploadedPhoto> = [];
        Object.keys(possibleDuplicateGroupedByFile).map((key: any) => {
            const groupContainsDuplicate = possibleDuplicateGroupedByFile[key].some(
                (x: IPossibleDuplicate) => x.isDuplicate,
            );
            if (!groupContainsDuplicate && possibleDuplicateGroupedByFile[key].length !== 0) {
                uniqueFiles.push(possibleDuplicateGroupedByFile[key][0].newPhoto);
            }
        });

        return uniqueFiles;
    }

    private renderDuplicatePrompt() {
        const itemToCheck = this.state.possibleDuplicates[this.state.duplicateIndex];

        return (
            <DuplicatePhotoPrompt
                fileName={itemToCheck.newPhoto.filename}
                newPhotoUrl={itemToCheck.newPhoto.image!.preview}
                matchingPhotoUrl={itemToCheck.originalPhoto.url}
                onClickIsDuplicate={() => this.onClickDuplicate(true)}
                onClickFalseDuplicate={() => this.onClickDuplicate(false)}
            />
        );
    }

    private async checkForDuplicateFileNames(
        uploadedFiles: Array<IUploadedPhoto>,
    ): Promise<{ uniquePhotos: Array<IUploadedPhoto>; possibleDuplicates: Array<IPossibleDuplicate> }> {

        if (!this.props.store) {
            return {uniquePhotos: [], possibleDuplicates: []};
        }
        await this.props.store.userPhotoStore.checkForDuplicatePhotos(uploadedFiles);
        const uniquePhotos: Array<IUploadedPhoto> = [];
        let possibleDuplicates: Array<IPossibleDuplicate> = [];
        const currentPhotos = this.getAllCurrentFileNamesAndUrls();

        uploadedFiles.forEach((newPhoto, index) => {
            const userPhotosWithSameFileName = currentPhotos.filter(p => p.name === newPhoto.filename);
            if (userPhotosWithSameFileName.length > 0) {
                const newPossibleDuplicates = userPhotosWithSameFileName.map(p => {
                    return { originalPhoto: p, newPhoto, tempID: index, isDuplicate: undefined };
                });
                possibleDuplicates = [...possibleDuplicates, ...newPossibleDuplicates];
            } else {
                uniquePhotos.push(newPhoto);
            }
        });

        return { uniquePhotos, possibleDuplicates };
    }

    private getAllCurrentFileNamesAndUrls(): Array<ISimplePhotoData> {
        const possibleDupes = [...this.props.store!.userPhotoStore.possibleDupes].map(p => {
            return { name: p.description.filename, url: p.url };
        });
        const queuedPhotos = [...this.state.images].map(i => {
            return { name: i.filename, url: i.image!.preview };
        });
        return [...possibleDupes, ...queuedPhotos];

    }

    private removeCurrentImage(): void {
        const state = [...this.state.images];
        state.splice(this.state.currentImageFocusIndex, 1);
        this.props.onPhotosChanged(state);
        this.setState({ images: state, currentImageFocusIndex: Math.max(this.state.currentImageFocusIndex - 1, 0) });
    }

    private uploadAllImages = async (): Promise<void> => {
        if (this.state.indexOfImageToUpload === 0) {
            this.setState({ showUploadProgress: true });
        }

        if (this.state.indexOfImageToUpload === this.state.images.length) {
            return this.props.store!.storyStore.addPhotosToStory(parseInt(this.props.match.params.id), this.state.idsToAddToStory)
                .then(async () => {
                    await this.props.store!.storyStore.getAllStories();
                    if (this.props.store!.userPhotoStore && this.props.store!.userPhotoStore.initialPhotosAreLoaded) {
                        console.log('already got photos, get again');
                        await this.props.store!.userPhotoStore.getAllPhotosFromServer();
                    } else {
                        console.log('have not fetched photos yet, so skip');
                    }
                    if (this.state.errors.length > 0) {
                        this.setState({showUploadErrorConfirmButton: true});
                        return;
                    }
                    this.props.onAfterPhotosAdded(this.state.idsToAddToStory);
                    this.setState({ fetchingAlbums: true });
                });
        }

        const indexToUpload = this.state.indexOfImageToUpload;
        await this.setState({ indexOfImageToUpload: this.state.indexOfImageToUpload + 1 });

        let imgSource = { ...this.state.images[indexToUpload] };
        imgSource.image = await getRotatedFileFromImage(imgSource.image.preview, imgSource.rotateDegrees);

        let thumbnailSource = { ...this.state.images[indexToUpload] };
        thumbnailSource.image = await compressImage(
          thumbnailSource.image.preview,
          THUMBNAIL_WIDTH_PIXELS,
          imgSource.rotateDegrees,
        );

        console.log('imgSource', imgSource);

        try {
            const uploadedPhoto = await this.props.store!.userPhotoStore.uploadPhoto(imgSource, thumbnailSource);
            const idsToAddToStory = [...this.state.idsToAddToStory];
            idsToAddToStory.push(uploadedPhoto.id);
            this.setState({ idsToAddToStory });
        } catch (error) {
            let errorText = `Error uploading ${imgSource.filename}: ${error}`;
            this.setState({errors: [...this.state.errors, errorText]});
            console.log(error);
        }
        return this.uploadAllImages();
    }

    private renderPhotoDataInput(description: ImageDescription) {
        let value = '';
        let header = '';
        let shouldDisable = false;
        let stateUpdateKey: keyof IUploadedPhoto = 'filename';
        let placeholder = '';
        let descriptionLink = '';

        const currentImage = this.state.images[this.state.currentImageFocusIndex];

        switch (description) {
            case ImageDescription.Date:
                stateUpdateKey = 'date';
                value = currentImage.date;
                placeholder = ui.DATE_PLACEHOLDER;
                header = ui.OCCURRED_WHEN;
                descriptionLink = FRESHDESK.WHEN;
                break;
            case ImageDescription.FileName:
                stateUpdateKey = 'filename';
                value = currentImage.filename;
                shouldDisable = true;
                placeholder = '';
                header = ui.FILE_NAME;
                break;
            case ImageDescription.Location:
                stateUpdateKey = 'location';
                value = currentImage.location;
                placeholder = ui.CITY_STATE;
                header = ui.OCCURRED_WHERE;
                descriptionLink = FRESHDESK.WHERE;
                break;
            default:
                assert(false, 'no case was found for @renderPhotoDataInput switch statement');
        }

        const updateImageState = (text: string) => {
            const img = { ...currentImage };
            img[stateUpdateKey] = text;
            const state = [...this.state.images];
            state[this.state.currentImageFocusIndex] = img;
            this.setState({ images: state });
        };

        function DeleteButton(props: any) {
            if (!props.show) {
                return null;
            }
            return (
                <div style={styles.inputCancel} onClick={() => updateImageState('')}>
                    <img src={ModalXIconInverse} style={styles.cancelIcon} />
                </div>
            );
        }

        return (
            <div style={styles.input(shouldDisable)}>
                <CommonTextInput
                    disabled={shouldDisable}
                    value={value}
                    containerStyle={styles.inputContainer}
                    onChangeText={updateImageState}
                    header={header}
                    placeholder={placeholder}
                    sideButton={() => (
                        <DeleteButton show={!shouldDisable} />
                    )}
                    helpLink={descriptionLink !== '' ? descriptionLink : undefined}
                />
            </div>
        );
    }

    private rotatePhoto(): void {
        const images = [...this.state.images];
        const photo = images[this.state.currentImageFocusIndex];
        photo.rotateDegrees = getNextRotation(photo.rotateDegrees);
        images[this.state.currentImageFocusIndex] = photo;
        this.setState({ images });
    }

    public render() {
        if (this.state.images.length === 0) {
            return (
                <FullContainer style={styles.paddedContainer}>
                    <div style={styles.wideUploadContainer}>
                        <Dropzone.default
                            ref={(ref: any) => {
                                this.dropzoneRef = ref;
                            }}
                            style={styles.innerDropZone}
                            onDrop={(files: any) => {
                                this.onDrop(files);
                            }}
                            accept={ACCEPT_IMAGE_TYPE}
                            disableClick={true}
                            onDropRejected={rejectedFiles => {
                                rejectedFiles.forEach(file => {
                                    alert(`${file.name} ${ui.NOT_SUPPORTED}`);
                                });
                            }}
                        >
                            <div style={styles.relative}>
                                <img src={UploadPhotoIcon} style={styles.uploadIcon} />
                                <InformationIcon link={FRESHDESK.OPTIONS_FOR_UPLOADING_PHOTOS} style={styles.iconLink} />
                            </div>

                            <p style={styles.actionText}>{ui.ACTION_TEXT}</p>
                            <p style={styles.subText}>{ui.SUB_TEXT}</p>
                            <CommonActionButton
                                buttonContent={ui.BUTTON_TEXT}
                                onClick={() => this.dropzoneRef.open()}
                                containerStyle={styles.button}
                                fontStyle={styles.buttonText}
                            />

                            {this.state.errors.length > 0 && (
                                <Errors errors={this.state.errors} />
                            )}
                        </Dropzone.default>
                    </div>
                    {this.state.possibleDuplicates.length !== 0 && this.renderDuplicatePrompt()}
                </FullContainer>
            );
        }

        return (
            <FullContainer style={styles.container}>
                <div style={styles.paddedHeader}>
                    <div style={styles.addPhotosContainer}>
                        <div style={styles.addPhotosLeft}>
                            <Dropzone.default
                                ref={(ref: any) => {
                                    this.dropzoneRef = ref;
                                }}
                                onDrop={(files: any) => {
                                    this.onDrop(files);
                                }}
                                accept={ACCEPT_IMAGE_TYPE}
                                disableClick={true}
                                className="customScrollBar"
                                style={styles.addPhotosUploader}
                            >
                                <div style={styles.wrapContainer}>
                                    {this.state.images.map((image, index) => {
                                        if (image.error) {
                                            return null;
                                        }
                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    ...styles.imageContainer,
                                                    ...(index === this.state.currentImageFocusIndex
                                                        ? styles.selectedImageContainer
                                                        : {}),
                                                }}
                                            >
                                                <img
                                                    src={image.image.preview}
                                                    style={{
                                                        ...styles.uploadedImage,
                                                        transform: `rotate(${image.rotateDegrees}deg)`,
                                                    }}
                                                    onClick={() => this.setState({ currentImageFocusIndex: index })}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </Dropzone.default>
                            <div style={styles.additionalUploadContainer}>
                                <img src={UploadPhotoIcon} style={styles.uploadPhotoIcon} />
                                <div style={styles.dropPhotoTextContainer}>
                                    <p style={styles.dropPhotoText}>{`${ui.DROP_MORE} (${
                                        this.state.images.length
                                    } / ${MAX_PHOTOS_PER_UPLOAD})`}</p>
                                    <p style={styles.uploadSubText}>{ui.SUB_TEXT}</p>
                                </div>
                                <CommonActionButton
                                    containerStyle={styles.chooseFilesButton}
                                    fontStyle={styles.smallFont}
                                    buttonContent={ui.CHOOSE_FILES}
                                    onClick={() => this.dropzoneRef.open()}
                                />
                            </div>
                        </div>

                        <div style={styles.addPhotosRight}>
                            <div style={styles.addPhotosInputsContainer}>
                                <p style={styles.storyTitle}>
                                    {ui.FOR_STORY +
                                        this.props.store!.storyStore.getStoryByID(parseInt(this.props.match.params.id)).title}
                                </p>
                                {this.renderPhotoDataInput(ImageDescription.Location)}
                                {this.renderPhotoDataInput(ImageDescription.Date)}
                                {this.renderPhotoDataInput(ImageDescription.FileName)}

                                <div style={styles.buttonRowContainer}>
                                    <CommonActionButton onClick={() => this.rotatePhoto()} buttonContent={ui.ROTATE} />
                                    <CommonActionButton
                                        onClick={() => this.removeCurrentImage()}
                                        buttonContent={ui.REMOVE}
                                        containerStyle={styles.deleteButton}
                                    />
                                </div>

                                {this.state.errors.length > 0 && (
                                    <Errors errors={this.state.errors} />
                                )}

                            </div>
                        </div>
                    </div>
                </div>

                <ModalActionButtonBar
                    confirmOnClick={() => this.uploadAllImages()}
                    confirmContent={ui.CONFIRM_TEXT}
                    style={styles.actionButtonBar}
                />

                {this.state.showUploadProgress && (
                    <div style={styles.uploadingPhotosContainer}>
                        <p style={styles.uploadingPhotosText}>{ui.UPLOADING_PHOTOS}</p>
                        <p style={styles.uploadingPhotosCount}>{`${this.state.indexOfImageToUpload} out of ${
                            this.state.images.length
                        }`}</p>

                        {this.state.fetchingAlbums && (
                            <p style={styles.uploadingPhotosCount}>Fetching Albums...</p>
                        )}

                        {this.state.errors.length > 0 && (
                            <React.Fragment>
                                <Errors
                                    errors={this.state.errors}
                                />
                                {this.state.showUploadErrorConfirmButton && (
                                    <CommonActionButton
                                        buttonContent="OK"
                                        onClick={() => this.props.onAfterPhotosAdded(this.state.idsToAddToStory)}
                                        containerStyle={styles.button}
                                        fontStyle={styles.buttonText}
                                    />
                                )}
                            </React.Fragment>
                        )}
                    </div>
                )}

                {this.state.showExtraPhotoNumber && (
                    <ExitableAlert
                        headerText={ui.CANNOT_UPLOAD_MORE(this.state.showExtraPhotoNumber)}
                        confirmText={ui.OK}
                        onConfirmClick={() => this.setState({ showExtraPhotoNumber: undefined })}
                    />
                )}

                {this.state.possibleDuplicates.length !== 0 && this.renderDuplicatePrompt()}
            </FullContainer>
        );
    }
}



// WEBPACK FOOTER //
// ./src/Pages/StoriesPage/StoryPhotoUploader.tsx